import { useTranslation } from 'react-i18next';
import { textGeneratorTool } from '../../../mocks/aiTools';

function StarDisplay({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <i key={i} className={`text-lg ${i <= Math.floor(rating) ? 'ri-star-fill text-amber-400' : i === Math.ceil(rating) ? 'ri-star-half-fill text-amber-400' : 'ri-star-line text-gray-300'}`}></i>
      ))}
      <span className="text-darkBlue-500 font-bold ml-1">{rating}</span>
    </div>
  );
}

export default function TextGeneratorReview() {
  const { t } = useTranslation();
  const tool = textGeneratorTool;

  return (
    <article>
      {/* Tool Header */}
      <div className="bg-white rounded-3xl p-8 border border-gray-100 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          <div className="flex items-start gap-5 flex-1">
            <div className="w-16 h-16 bg-gradient-to-br from-clairBlue-200 to-clairBlue-400 rounded-2xl flex items-center justify-center flex-shrink-0">
              <i className="ri-quill-pen-line text-white text-2xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <h2 className="font-display text-2xl font-bold text-darkBlue-500">{tool.name}</h2>
                <span className="bg-clairBlue-50 text-clairBlue-600 text-xs font-semibold px-3 py-1 rounded-full">{tool.category}</span>
              </div>
              <p className="text-gray-500 text-sm mb-3">{tool.tagline}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <StarDisplay rating={tool.rating} />
                <span className="text-gray-400 text-sm">({tool.reviewCount.toLocaleString()} reviews)</span>
                {tool.freeTrialAvailable && (
                  <span className="flex items-center gap-1 text-green-600 text-xs font-medium bg-green-50 px-2.5 py-1 rounded-full">
                    <i className="ri-gift-line"></i> Free Trial Available
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3 flex-shrink-0">
            <a
              href={tool.affiliateLink}
              target="_blank"
              rel="nofollow"
              className="flex items-center justify-center gap-2 bg-clairBlue-300 hover:bg-clairBlue-400 text-darkBlue-500 font-bold px-7 py-3.5 rounded-xl transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i> {t('review.tryAffiliate')}
            </a>
            <div className="text-center text-xs text-gray-400">Best for: {tool.bestFor.split(',')[0]}</div>
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div className="rounded-3xl overflow-hidden mb-8 h-72">
        <img src={tool.image} alt={tool.name} className="w-full h-full object-cover object-top" />
      </div>

      {/* Quick Overview */}
      <div className="bg-clairBlue-50 rounded-2xl p-6 mb-8 border border-clairBlue-100">
        <h3 className="font-display text-lg font-bold text-darkBlue-500 mb-4">{t('review.overview')}</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t('review.ourRating'), value: `${tool.rating}/5.0`, icon: 'ri-star-fill' },
            { label: t('review.bestFor'), value: 'Marketers & Writers', icon: 'ri-user-heart-line' },
            { label: t('review.freeTrialAvail'), value: t('review.yes'), icon: 'ri-gift-line' },
            { label: 'Starting Price', value: '$49/mo', icon: 'ri-price-tag-3-line' },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border border-clairBlue-100 text-center">
              <i className={`${item.icon} text-clairBlue-400 text-xl mb-2 block`}></i>
              <div className="text-darkBlue-500 font-bold text-sm">{item.value}</div>
              <div className="text-gray-500 text-xs">{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Description */}
      <div className="bg-white rounded-2xl p-8 mb-8 border border-gray-100">
        <h3 className="font-display text-xl font-bold text-darkBlue-500 mb-4">About {tool.name}</h3>
        <p className="text-gray-600 leading-relaxed">{tool.description}</p>
      </div>

      {/* Features */}
      <div className="bg-white rounded-2xl p-8 mb-8 border border-gray-100">
        <h3 className="font-display text-xl font-bold text-darkBlue-500 mb-6">{t('review.features')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {tool.features.map((feature, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-clairBlue-50 rounded-xl flex-shrink-0">
                <i className={`${feature.icon} text-clairBlue-500`}></i>
              </div>
              <div>
                <h4 className="font-semibold text-darkBlue-500 text-sm mb-1">{feature.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pros & Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-green-50 rounded-2xl p-6 border border-green-100">
          <h3 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
            <i className="ri-thumb-up-line text-green-600"></i> {t('review.pros')}
          </h3>
          <ul className="space-y-2.5">
            {tool.pros.map((pro, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-green-800">
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 mt-0.5 flex-shrink-0">
                  <i className="ri-check-line text-green-600 text-xs"></i>
                </div>
                {pro}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
          <h3 className="font-semibold text-red-800 mb-4 flex items-center gap-2">
            <i className="ri-thumb-down-line text-red-500"></i> {t('review.cons')}
          </h3>
          <ul className="space-y-2.5">
            {tool.cons.map((con, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-red-800">
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-red-100 mt-0.5 flex-shrink-0">
                  <i className="ri-close-line text-red-500 text-xs"></i>
                </div>
                {con}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Step by Step */}
      <div className="bg-white rounded-2xl p-8 mb-8 border border-gray-100">
        <h3 className="font-display text-xl font-bold text-darkBlue-500 mb-6">{t('review.guide')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {tool.steps.map((step, i) => (
            <div key={i} className="bg-brokenWhite-100 rounded-xl overflow-hidden border border-gray-100">
              <div className="h-36 overflow-hidden">
                <img src={step.image} alt={step.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-clairBlue-300 text-darkBlue-500 text-xs font-bold rounded-full">{step.step}</div>
                  <h4 className="font-semibold text-darkBlue-500 text-sm">{step.title}</h4>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div className="bg-white rounded-2xl p-8 mb-8 border border-gray-100">
        <h3 className="font-display text-xl font-bold text-darkBlue-500 mb-6">{t('review.pricing')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {tool.pricing.map((plan, i) => (
            <div key={i} className={`rounded-xl p-6 border-2 ${plan.recommended ? 'border-clairBlue-300 bg-clairBlue-50' : 'border-gray-100'}`}>
              {plan.recommended && <span className="inline-block bg-clairBlue-300 text-darkBlue-500 text-xs font-bold px-3 py-1 rounded-full mb-3">Best Value</span>}
              <div className="font-semibold text-gray-600 text-sm mb-1">{plan.name}</div>
              <div className="flex items-end gap-1 mb-4">
                <span className="font-display text-3xl font-bold text-darkBlue-500">{plan.price}</span>
                <span className="text-gray-400 text-sm mb-1">{plan.period}</span>
              </div>
              <ul className="space-y-2 mb-5">
                {plan.features.map((f, j) => (
                  <li key={j} className="text-xs text-gray-600 flex items-center gap-2">
                    <i className="ri-check-line text-clairBlue-500 flex-shrink-0"></i>{f}
                  </li>
                ))}
              </ul>
              <a
                href={tool.affiliateLink}
                target="_blank"
                rel="nofollow"
                className={`w-full py-2.5 rounded-lg text-sm font-semibold text-center block transition-all ${plan.recommended ? 'bg-darkBlue-500 text-white hover:bg-darkBlue-600' : 'bg-gray-100 text-darkBlue-500 hover:bg-gray-200'}`}
              >
                Select Plan
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Verdict */}
      <div className="bg-darkBlue-500 rounded-2xl p-8 mb-6 border-l-8 border-clairBlue-300">
        <h3 className="font-display text-xl font-bold text-white mb-3 flex items-center gap-2">
          <i className="ri-award-line text-clairBlue-300"></i> {t('review.verdict')}
        </h3>
        <p className="text-gray-300 leading-relaxed mb-5">{tool.verdict}</p>
        <div className="flex items-center gap-4 flex-wrap">
          <StarDisplay rating={tool.verdictRating} />
          <a
            href={tool.affiliateLink}
            target="_blank"
            rel="nofollow"
            className="flex items-center gap-2 bg-clairBlue-300 hover:bg-clairBlue-200 text-darkBlue-500 font-bold px-7 py-3.5 rounded-xl transition-all hover:scale-105 whitespace-nowrap"
          >
            <i className="ri-external-link-line"></i> {t('review.getStarted')} {tool.name}
          </a>
        </div>
      </div>
    </article>
  );
}
